import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { registrarTypeSafe, CONFIANZA_MINIMA } from '../lib/typesafe';
import { claveImpresion } from '../data/impresion';
import Button from '../ds/components/Button';
import LoadingDots from '../ds/components/LoadingDots';
import MarcoMarca from './MarcoMarca';
import './WebChat.css';

// ── Tagline generation (same logic as Dashboard) ──────────────────────────
const generateTagline = (nombre, feeling, descripcion) => {
  const n = nombre?.trim() || '';
  const d = descripcion?.trim() || '';
  const km = d.match(/(?:somos|hacemos|ofrecemos|brindamos|ayudamos a|me dedico a|trabajamos en)\s+([^,.]+)/i);
  const kw = km ? km[1].trim().toLowerCase() : '';
  const T = {
    exacto: n ? `${n}: exactamente lo que estabas buscando.` : 'Exactamente lo que estabas buscando.',
    saber: n ? `Hay algo distinto en ${n}.` : 'Algo que vale la pena conocer.',
    confianza: n ? `${n}. El lugar en el que podés confiar.` : 'El lugar en el que podés confiar.',
    ahora: n ? `Cuando lo necesitás, ${n} está.` : 'Cuando lo necesitás, estamos.',
  };
  return T[claveImpresion(feeling)] || (kw ? `${kw.charAt(0).toUpperCase() + kw.slice(1)} con ${n || 'nosotros'}.` : (n ? `${n}. Tecnología que sirve.` : 'Tecnología que sirve.'));
};

const parseServicios = (raw) => {
  if (!raw?.trim()) return [{ title: '', desc: '' }];
  return raw.split(/[,;|\n]+/).map(s => ({ title: s.trim(), desc: '' })).filter(s => s.title.length > 0);
};

// ── Conversation steps ─────────────────────────────────────────────────────
const buildSteps = (passState) => {
  const { businessName, businessInfo, feeling, rubro, rubroSugerido } = passState || {};
  // Dudó: tenía sugerencia pero no llegó al umbral (y no era "Otro").
  const opciones = rubroSugerido?.opciones || [];
  const dudo = !rubro && rubroSugerido && rubroSugerido.valor !== 'Otro' && opciones.length >= 2;
  const nombre    = businessName?.trim() || '';
  const suggested = generateTagline(nombre, feeling, businessInfo || '');

  return [
    // 0 — opening (no input)
    {
      key: null,
      aiText: nombre
        ? `Ya tengo el nombre: ${nombre}. Ahora necesito unos datos más.\n\nSon 5 preguntas.`
        : 'Hola. Voy a hacerte 5 preguntas para armar tu web.',
      needsInput: false,
    },
    // 1 — rubro
    {
      key: 'rubro',
      // rubro llega pre-llenado desde lo que contó en "¿Qué hacés?" (TypeSafe,
      // ver netlify/functions/clasificar.mjs). Queda editable: si no va, lo cambia.
      aiText: rubro
        ? `¿Con qué categoría describirías tu negocio?\n\nPor lo que me contaste, puse "${rubro}". Cambiala si preferís otra. Aparece como rubro en la web.`
        : dudo
          ? `¿Con qué categoría describirías tu negocio?\n\nPor lo que me contaste dudé entre "${opciones[0].valor}" y "${opciones[1].valor}", así que prefiero preguntarte. Una o dos palabras; aparece como rubro en la web.`
          : '¿Con qué categoría describirías tu negocio?\n\nUna o dos palabras. Aparece como rubro en la web.',
      // "¿Cómo lo supe?": cómo decidió TypeSafe, con la línea del umbral.
      explicacion: (rubro || dudo) && opciones.length ? opciones : null,
      placeholder: 'Ej: Psicología · Gastronomía · Arquitectura',
      ack: (v) => `${v}. Anotado.`,
      needsInput: true,
      defaultVal: rubro || '',
    },
    // 2 — tagline
    {
      key: 'tagline',
      aiText: `¿Cuál es la frase que define tu negocio?\n\nTe sugiero esta:\n"${suggested}"\n\nPodés usarla, editarla o escribir la tuya.`,
      placeholder: suggested,
      ack: () => 'Anotada.',
      needsInput: true,
      defaultVal: suggested,
    },
    // 3 — servicios
    {
      key: 'servicios_raw',
      aiText: '¿Qué servicios o productos ofrecés?\n\nEscribí los principales, separados por coma.',
      placeholder: 'Ej: Terapia individual, terapia de pareja, talleres grupales',
      ack: (v) => {
        const count = v.split(/[,;]+/).filter(s => s.trim()).length;
        return count > 1 ? `${count} servicios. Los tengo.` : 'Anotado.';
      },
      needsInput: true,
      defaultVal: '',
    },
    // 4 — ubicacion
    {
      key: 'ubicacion',
      aiText: '¿Dónde estás?',
      placeholder: 'Ej: Córdoba Capital · Buenos Aires · Rosario',
      ack: (v) => `${v}. Anotado.`,
      needsInput: true,
      defaultVal: '',
    },
    // 5 — contacto
    {
      key: 'contacto',
      aiText: '¿Cómo pueden contactarte?\n\nEmail, teléfono o WhatsApp: lo que uses.',
      placeholder: 'Ej: hola@tunegocio.com.ar · +54 9 351 123-4567',
      ack: () => 'Anotado.',
      needsInput: true,
      defaultVal: '',
    },
    // 6 — closing (no input)
    {
      key: null,
      aiText: 'Con tus respuestas ya puedo mostrarte la vista previa de tu web.\n\n¿La vemos?',
      needsInput: false,
      isDone: true,
    },
  ];
};

// ── Etiqueta visible de cada campo (el placeholder no hace de label) ────────
const ETIQUETAS = {
  rubro: 'Rubro',
  tagline: 'Frase de tu negocio',
  servicios_raw: 'Servicios o productos',
  ubicacion: 'Ubicación',
  contacto: 'Contacto',
};

// ── Burbuja de GUIA escribiendo ─────────────────────────────────────────────
const Escribiendo = () => (
  <div className="wc-fila wc-fila--guia">
    <div className="wc-burbuja wc-burbuja--guia wc-burbuja--puntos" data-theme="claro">
      <LoadingDots size={9} aria-label="GUIA está escribiendo" />
    </div>
  </div>
);

// ── "¿Cómo lo supe?" ───────────────────────────────────────────────────────
// Muestra cómo decidió la IA: las 3 opciones más probables y la línea del
// umbral. Arriba de la línea completa sola; abajo, pregunta. Base: Nielsen,
// visibilidad del estado del sistema. Que mostrar la duda genere confianza es
// intuición, sin respaldo confirmado.
const ComoLoSupe = ({ opciones }) => {
  const [abierto, setAbierto] = useState(false);
  const umbral = Math.round(CONFIANZA_MINIMA * 100);
  return (
    <div className="wc-como">
      <button
        type="button"
        onClick={() => setAbierto(a => !a)}
        aria-expanded={abierto}
        className="wc-como__boton"
      >
        {abierto ? 'Ocultar' : '¿Cómo lo supe?'}
      </button>
      {abierto && (
        <div className="wc-como__detalle">
          <p>No escribí nada: elegí entre rubros posibles y medí qué tan segura estaba de cada uno.</p>
          {opciones.map(o => {
            const pct = Math.round(o.p * 100);
            const pasa = pct >= umbral;
            return (
              <div key={o.valor} className="wc-como__fila">
                <div className="wc-como__dato">
                  <span>{o.valor}</span>
                  <span className="wc-como__pct">
                    {pct}%<span className="wc-sr">{pasa ? ', pasa la línea' : ', no llega a la línea'}</span>
                  </span>
                </div>
                <div className="wc-como__pista" aria-hidden="true">
                  <div className="wc-como__relleno" data-pasa={pasa} style={{ width: `${pct}%` }} />
                  <div className="wc-como__umbral" style={{ left: `${umbral}%` }} />
                </div>
              </div>
            );
          })}
          <p>
            La línea es el {umbral}%. Si una opción la pasa, la completo; si no, te pregunto. Prefiero dejarte el campo vacío antes que ponerte uno equivocado.
          </p>
        </div>
      )}
    </div>
  );
};

// ── Mensaje de GUIA ─────────────────────────────────────────────────────────
const AiMessage = ({ text, isDone, explicacion, onNavigate }) => {
  const accionRef = useRef(null);
  // Al terminar no queda campo: el foco pasa al botón que sigue.
  useEffect(() => {
    if (isDone) accionRef.current?.querySelector('button')?.focus();
  }, [isDone]);

  return (
    <div className="wc-fila wc-fila--guia">
      <div className="wc-burbuja wc-burbuja--guia">
        <p className="wc-texto">{text}</p>
        {explicacion && <ComoLoSupe opciones={explicacion} />}
        {isDone && (
          <div className="wc-accion" ref={accionRef}>
            <Button variant="solido" onClick={onNavigate}>Ver la vista previa</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Mensaje de la persona ───────────────────────────────────────────────────
const UserMessage = ({ text }) => (
  <div className="wc-fila wc-fila--yo">
    <div className="wc-burbuja wc-burbuja--yo">
      <p className="wc-texto">{text}</p>
    </div>
  </div>
);

// ── Avance ──────────────────────────────────────────────────────────────────
const ChatProgress = ({ current, total }) => {
  const hechas = Math.min(current, total);
  return (
    <div className="wc-avance">
      <div
        className="gh-progreso"
        role="progressbar"
        aria-label="Avance de las preguntas"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={hechas}
        aria-valuetext={`${hechas} de ${total} preguntas respondidas`}
      >
        {Array.from({ length: total }).map((_, i) => (
          <i key={i} data-hecho={i < hechas} />
        ))}
      </div>
      <span className="wc-avance__texto" aria-hidden="true">{hechas} de {total}</span>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const WebChat = () => {
  const { state }  = useLocation();
  const navigate   = useNavigate();
  const passState  = state || {};

  const [steps]            = useState(() => buildSteps(passState));
  const [messages, setMsg] = useState([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [aiTyping, setAiTyping]   = useState(false);
  const [inputEnabled, setInputEnabled] = useState(false);
  const [inputVal, setInputVal]   = useState('');
  const [collected, setCollected] = useState({});
  const [answered, setAnswered]   = useState(0); // questions answered

  const endRef   = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef([]);

  const addMsg = useCallback((msg) => {
    setMsg(prev => [...prev, { ...msg, id: `${Date.now()}-${Math.random()}` }]);
  }, []);

  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timerRef.current.push(id);
    return id;
  }, []);

  // Scroll to bottom on new messages (sin animación si la persona pidió menos movimiento)
  useEffect(() => {
    const quieto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    endRef.current?.scrollIntoView({ behavior: quieto ? 'auto' : 'smooth', block: 'end' });
  }, [messages, aiTyping]);

  // Focus input when enabled
  useEffect(() => {
    if (inputEnabled) inputRef.current?.focus();
  }, [inputEnabled]);

  // Cleanup
  useEffect(() => () => timerRef.current.forEach(clearTimeout), []);

  // Deliver AI step
  const deliverStep = useCallback((idx) => {
    if (idx >= steps.length) return;
    const step = steps[idx];

    setAiTyping(true);
    setInputEnabled(false);

    later(() => {
      setAiTyping(false);
      addMsg({ role: 'ai', text: step.aiText, isDone: step.isDone || false, explicacion: step.explicacion || null });

      if (step.needsInput) {
        setStepIdx(idx);
        setInputVal(step.defaultVal || '');
        setInputEnabled(true);
      } else if (!step.isDone) {
        // Info message — auto-advance after short pause
        later(() => deliverStep(idx + 1), 600);
      }
    }, 900 + Math.random() * 400);
  }, [steps, addMsg, later]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start on mount
  useEffect(() => {
    later(() => deliverStep(0), 400);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle user answer
  const handleSubmit = (e) => {
    e.preventDefault();
    const val = inputVal.trim();
    if (!val || !inputEnabled) return;

    const step = steps[stepIdx];
    addMsg({ role: 'user', text: val });
    setInputEnabled(false);
    setInputVal('');

    const newCollected = { ...collected, [step.key]: val };
    setCollected(newCollected);

    // Lo que sugirió TypeSafe contra lo que la persona dejó: el caso real para mejorar.
    const sugerido = passState.rubroSugerido;
    if (step.key === 'rubro' && sugerido && passState.businessInfo) {
      registrarTypeSafe({
        pregunta: 'rubro',
        texto: passState.businessInfo,
        sugerencia: sugerido.valor,
        confianza: sugerido.confianza,
        final: val,
      });
    }
    setAnswered(a => a + 1);

    // Ack
    setAiTyping(true);
    later(() => {
      setAiTyping(false);
      if (step.ack) addMsg({ role: 'ai', text: step.ack(val) });

      const nextIdx = stepIdx + 1;
      setStepIdx(nextIdx);
      later(() => deliverStep(nextIdx), 350);
    }, 600 + Math.random() * 250);
  };

  // Navigate to dashboard with all collected data
  const handleNavigate = () => {
    const merged = {
      // From original flow
      ...passState,
      // From chat
      chatCollected: {
        rubro:     collected.rubro     || '',
        tagline:   collected.tagline   || '',
        servicios: parseServicios(collected.servicios_raw),
        ubicacion: collected.ubicacion || '',
        contacto:  collected.contacto  || '',
      },
    };
    navigate('/dashboard', { state: merged });
  };

  const totalQuestions = steps.filter(s => s.needsInput).length;
  const terminado = messages.some(m => m.isDone);
  const claveActual = steps[stepIdx]?.key;
  const etiqueta = inputEnabled && ETIQUETAS[claveActual] ? ETIQUETAS[claveActual] : 'Tu respuesta';
  const nombre = passState.businessName?.trim();

  const lado = (
    <span className="gh-pildora">
      <button type="button" className="gh-link wc-cambiar" onClick={() => navigate('/web-start', { state: passState })}>
        Cambiar de modo
      </button>
    </span>
  );

  return (
    <MarcoMarca pantalla="web-chat" lado={lado}>
      <div className="gh-panel wc-panel" data-fase="charla">
        <header className="wc-cabecera">
          <h1 className="wc-titulo">{nombre ? `Web para ${nombre}` : 'Tu web'}</h1>
          <ChatProgress current={answered} total={totalQuestions} />
        </header>

        <div className="wc-charla" role="log" aria-live="polite" aria-label="Conversación con GUIA">
          {messages.map(msg =>
            msg.role === 'ai'
              ? <AiMessage key={msg.id} text={msg.text} isDone={msg.isDone} explicacion={msg.explicacion} onNavigate={handleNavigate} />
              : <UserMessage key={msg.id} text={msg.text} />
          )}
        </div>

        {aiTyping && <Escribiendo />}

        {!terminado && (
          <form onSubmit={handleSubmit} className="wc-form">
            <label htmlFor="wc-respuesta" className="wc-etiqueta">{etiqueta}</label>
            <div className="gh-campo">
              <input
                id="wc-respuesta"
                ref={inputRef}
                type="text"
                name="respuesta"
                autoComplete="off"
                value={inputVal}
                onChange={e => setInputVal(e.target.value)}
                placeholder={
                  inputEnabled
                    ? (steps[stepIdx]?.placeholder || 'Escribí tu respuesta…')
                    : aiTyping ? 'GUIA está escribiendo…' : ''
                }
                disabled={!inputEnabled}
                className="gh-input"
              />
              <Button variant="solido" type="submit" disabled={!inputEnabled || !inputVal.trim()}>
                Responder
              </Button>
            </div>
          </form>
        )}

        <div ref={endRef} className="wc-fin" />
      </div>
    </MarcoMarca>
  );
};

export default WebChat;
